import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FileAction, IFileDto } from '../../../config/fileConfig'
import { DataPromise } from '../../../context/AxiosContext'
import * as Yup from 'yup'
import Relationship, { RelationshipType } from '../../../components/Form/FamilyLaw/Relationship'
import { Accordion, AccordionItem, Button, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { FamilyLawModuleDto, mapFamilyLawDto } from '../../../hooks/module/useFamilyLaw'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import { useFileModule } from '../../../hooks/module/useFileModule'
import InfoCard from '../../../components/Form/FamilyLaw/InfoCard'
import { Offspring } from '../../../components/Form/FamilyLaw/Offspring/Offspring'
import Requests from '../../../components/Form/FamilyLaw/Requests/Requests'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { Formik, useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import Loading from '../../../components/Loading'
import FormContainer from '../../../components/Form/FormContainer'
import Form from '../../../components/Form/Form'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { ModuleType } from '../../../context/GlobalContext'

type Props = {
    file: DataPromise<IFileDto>
}

export type FamilyLawAccordionItemProps = {
    useColumns: boolean
    styles: Record<
        | 'container'
        | 'horizontalGap'
        | 'buttons'
        | 'relationBase'
        | 'relationLocation'
        | 'offspringNameMain'
        | 'offspringNameGrid'
        | 'offspringInfo'
        | 'alignColumnEnd',
        string
    >
}

export type FamilyLawDialogProps = FamilyLawAccordionItemProps &
    FieldArrayRenderProps & {
        open: boolean
        setOpen: (open: boolean) => void
        action: FamilyLawDialogAction
        index?: number
    }

export enum FamilyLawDialogAction {
    Add = 'ADD',
    Edit = 'EDIT',
}

const validationSchema = Yup.object().shape({
    relationshipDate: Yup.string().trim().required('Geen datum vastlegging relatie'),
    relationshipRecord: Yup.string().required(),
    relationshipLocation: Yup.string().when('relationshipType', {
        is: value => value !== RelationshipType.Cohabitation,
        then: schema => schema.required('Plaats is verplicht'),
        otherwise: schema => schema.notRequired(),
    }),
    relationshipCountry: Yup.string().when('relationshipType', {
        is: value => value !== RelationshipType.Cohabitation,
        then: schema => schema.required('Land is verplicht'),
        otherwise: schema => schema.notRequired(),
    }),
})

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    horizontalGap: {
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    },
    buttons: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalS),
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignContent: 'flex-end',
    },
    relationBase: {
        display: 'grid',
        gridTemplateColumns: '3fr 3fr 1fr',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    relationLocation: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    offspringNameMain: {
        display: 'grid',
    },
    offspringNameGrid: {
        gridTemplateColumns: '1fr 4fr',
    },
    offspringInfo: {
        justifyContent: 'end',
    },
    alignColumnEnd: {
        textAlign: 'end',
    },
})

function Buttons({ styles }) {
    const { submitForm, resetForm } = useFormikContext<FamilyLawModuleDto>()
    return (
        <div className={styles.buttons}>
            <Button appearance="primary" onClick={submitForm} icon={<CheckmarkRegular />}>
                Opslaan
            </Button>
            <Button icon={<DismissRegular />} onClick={() => resetForm()}>
                Annuleren
            </Button>
        </div>
    )
}

export default function FamilyLawOverview(props: Props): ReactElement {
    const { isViewport, viewport } = useViewport()
    const useColumns = useMemo(() => isViewport('>=', Viewport.XL), [viewport])
    const styles = useStyles()
    const [values, setValues] = useState<FamilyLawModuleDto>(undefined)
    const axios = useAxios()
    const dispatch = useDispatch()

    const fileData = useMemo(() => (props.file.loaded ? props.file.data : undefined), [props.file.loaded])
    const { fileModule: familyLawModule, setCurrentFile } = useFileModule<FamilyLawModuleDto>(
        ModuleType.FamilyLaw,
        fileData,
    )

    useEffect(() => {
        if (fileData) {
            setCurrentFile(fileData)
        }
    }, [fileData])

    useEffect(() => {
        if (familyLawModule) {
            setValues(mapFamilyLawDto(familyLawModule))
        }
    }, [familyLawModule])

    if (!props.file.loaded) {
        return <Loading />
    }

    return (
        <Formik<FamilyLawModuleDto>
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            initialValues={values}
            enableReinitialize
            onSubmit={(values: FamilyLawModuleDto) =>
                axios
                    .put(`/family/${values.id}`, mapFamilyLawDto(values))
                    .then(() => dispatch({ type: FileAction.ResetCurrent }))
            }
        >
            <Form>
                <FormContainer sidePanel={<InfoCard />}>
                    <div className={styles.container}>
                        <Accordion multiple collapsible>
                            <AccordionItem value="relationship">
                                <Relationship useColumns={useColumns} styles={styles} />
                            </AccordionItem>
                            <AccordionItem value="offspring">
                                <Offspring useColumns={useColumns} styles={styles} />
                            </AccordionItem>
                            <AccordionItem value="request">
                                <Requests useColumns={useColumns} styles={styles} />
                            </AccordionItem>
                        </Accordion>
                        <Buttons styles={styles} />
                    </div>
                </FormContainer>
            </Form>
        </Formik>
    )
}
